import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { useNavigateWithParams } from '@experiences/util';
import {
    Box,
    styled,
} from '@mui/material';
import { FontVariantToken } from '@uipath/apollo-core';
import { ApButton } from '@uipath/portal-shell-react';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { routePaths } from '../../../../common/constants/routePaths';
import { GovernancePolicyStepHeader } from '../stepper/GovernancePolicyStepHeader';

const PolicyCard = styled(Box)(() => ({
    flex: '1 1 0',
    minWidth: '280px',
    maxWidth: '320px',
    border: '1px solid var(--color-border-default)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-background-default)',
    minHeight: '156px',
    padding: SpacingToken.L,
}));

interface PolicyCreationCard {
    id: string;
    title: string;
    description: string;
    buttonLabel: string;
    buttonVariant: 'primary' | 'secondary' | 'tertiary';
    type: 'template';
    templateIconText: string;
}

interface GovernancePolicyCreateStartProps {
    onStartStepper: () => void;
    /** Where Cancel navigates. Defaults to the v1 admin governance list when omitted. */
    returnUrl?: string;
}

export const GovernancePolicyCreateSelectFlow: React.FC<GovernancePolicyCreateStartProps> = ({
    onStartStepper, returnUrl,
}) => {
    const { formatMessage: translate } = useIntl();
    const navigate = useNavigateWithParams();

    const handleSelectFlow = useCallback((_option: PolicyCreationCard) => {
        // Start the stepper when user selects a policy creation option
        onStartStepper?.();
    }, [ onStartStepper ]);

    const policyCreationCards: PolicyCreationCard[] = [
        {
            id: 'template-1',
            type: 'template',
            title: translate({ id: 'CLIENT_GOVERNANCE_AGENT_ACCESS_TITLE' }),
            description: translate({ id: 'CLIENT_GOVERNANCE_AGENT_ACCESS_DESCRIPTION' }),
            buttonLabel: translate({ id: 'CLIENT_START_HERE' }),
            buttonVariant: 'secondary',
            templateIconText: translate({ id: 'CLIENT_GOVERNANCE_ACCESS_POLICY_TEXT' }),
        },
        {
            id: 'template-2',
            type: 'template',
            title: translate({ id: 'CLIENT_GOVERNANCE_FINANCE_ACCESS_TITLE' }),
            description: translate({ id: 'CLIENT_GOVERNANCE_FINANCE_ACCESS_DESCRIPTION' }),
            buttonLabel: translate({ id: 'CLIENT_START_HERE' }),
            buttonVariant: 'secondary',
            templateIconText: translate({ id: 'CLIENT_GOVERNANCE_ACCESS_POLICY_TEXT' }),
        },
        {
            id: 'template-3',
            type: 'template',
            title: translate({ id: 'CLIENT_GOVERNANCE_DATA_RETENTION_TITLE' }),
            description: translate({ id: 'CLIENT_GOVERNANCE_DATA_RETENTION_DESCRIPTION' }),
            buttonLabel: translate({ id: 'CLIENT_START_HERE' }),
            buttonVariant: 'secondary',
            templateIconText: translate({ id: 'CLIENT_GOVERNANCE_DATA_RETENTION_POLICY_TEXT' }),
        },
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
            data-cy="governance-policy-create-select-flow"
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: SpacingToken.XL,
                    paddingTop: '160px',
                    paddingLeft: '260px',
                    paddingRight: '260px',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    flex: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: SpacingToken.XL,
                        alignItems: 'flex-start',
                        width: 'min-content',
                    }}
                    data-cy="governance-policy-create-select-flow-content"
                >
                    <GovernancePolicyStepHeader
                        title={translate({ id: 'CLIENT_GOVERNANCE_SELECT_FLOW_TITLE' })}
                        description={translate({ id: 'CLIENT_GOVERNANCE_SELECT_FLOW_DESCRIPTION' })} />
                    <UiStack
                        direction="row"
                        gap={SpacingToken.L}
                        justify="start"
                        data-cy="governance-policy-creation-cards"
                    >
                        {policyCreationCards.map((card) => (
                            <PolicyCard
                                key={card.id}
                                data-cy={`governance-policy-card-${card.id}`}
                            >
                                <UiStack
                                    direction="column"
                                    gap={SpacingToken.XS}
                                    h="100%"
                                    justify="between"
                                >
                                    <UiStack
                                        direction="column"
                                        gap={SpacingToken.XS}
                                        align="start"
                                    >
                                        <UiText
                                            variant={FontVariantToken.fontSizeLBold}
                                            data-cy={`governance-policy-card-${card.id}-title`}
                                        >
                                            {card.title}
                                        </UiText>
                                        <UiText
                                            variant={FontVariantToken.fontSizeM}
                                            color="var(--color-foreground-de-emp)"
                                            data-cy={`governance-policy-card-${card.id}-description`}
                                        >
                                            {card.description}
                                        </UiText>
                                    </UiStack>

                                    <ApButton
                                        size='small'
                                        style={{ width: 'min-content' }}
                                        variant={card.buttonVariant}
                                        onClick={() => handleSelectFlow(card)}
                                        data-cy={`governance-policy-card-${card.id}-button`}
                                        label={card.buttonLabel}
                                    />
                                </UiStack>
                            </PolicyCard>
                        ))}
                    </UiStack>
                </Box>
            </Box>
            <Box
                sx={{
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '10px',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    borderTop: '1px solid var(--color-border-de-emp)',
                }}
                data-cy="governance-policy-select-flow-navigation"
            >
                <ApButton
                    variant="secondary"
                    onClick={() => navigate(returnUrl ?? routePaths.adminGovernance)}
                    data-cy="governance-policy-select-flow-cancel-button"
                    label={translate({ id: 'CLIENT_CANCEL' })}
                />
            </Box>
        </Box>
    );
};
